import React, { useState, useRef, useEffect } from 'react';

const ImageCropModal = ({ image, onConfirm, onCancel, isUploading }) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [error, setError] = useState('');
  const imageRef = useRef(null);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  // Réinitialiser l'état quand l'image change et vérifier si déjà chargée
  useEffect(() => {
    setIsImageLoaded(false);
    setError('');
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    
    // Vérifier si l'image est déjà chargée (cache) après un court délai
    const timer = setTimeout(() => {
      const img = imageRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        console.log('[ImageCrop] Image déjà en cache, dimensions:', img.naturalWidth, 'x', img.naturalHeight);
        setIsImageLoaded(true);
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [image]);

  const handleImageLoad = () => {
    console.log('[ImageCrop] Image chargée, dimensions:', 
      imageRef.current?.naturalWidth, 'x', imageRef.current?.naturalHeight);
    setIsImageLoaded(true);
    setError('');
  };

  const handleImageError = () => {
    console.error('[ImageCrop] Erreur chargement image');
    setError('Erreur lors du chargement de l\'image');
    setIsImageLoaded(false);
  };

  const handleConfirm = () => {
    const img = imageRef.current;
    
    if (!img || !isImageLoaded) {
      console.error('[ImageCrop] Image non chargée, impossible de générer le blob');
      setError('Veuillez attendre le chargement complet de l\'image');
      return;
    }

    if (!img.naturalWidth || !img.naturalHeight) {
      console.error('[ImageCrop] Image sans dimensions naturelles');
      setError('Image invalide, veuillez réessayer');
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Taille de sortie standard haute résolution
    const outputSize = 600;
    canvas.width = outputSize;
    canvas.height = outputSize;

    const containerSize = 280; // Correspond au CSS
    const scale = outputSize / containerSize;

    // On simule le 'object-fit: contain' pour le calcul du canvas
    const initialScale = Math.min(containerSize / img.naturalWidth, containerSize / img.naturalHeight);

    const finalScale = initialScale * zoom * scale;

    ctx.save();
    ctx.translate(outputSize / 2, outputSize / 2);
    // On applique le zoom et le positionnement
    ctx.scale(finalScale, finalScale);
    // Le positionnement doit être mis à l'échelle également
    ctx.translate(position.x / (initialScale * zoom), position.y / (initialScale * zoom));
    
    // Dessiner l'image centrée sur son propre axe
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    ctx.restore();

    canvas.toBlob((blob) => {
      if (!blob || blob.size === 0) {
        console.error('[ImageCrop] Blob généré est vide');
        setError('Erreur lors de la génération de l\'image');
        return;
      }
      console.log('[ImageCrop] Blob généré avec succès, taille:', blob.size);
      onConfirm(blob);
    }, 'image/jpeg', 0.95);
  };

  return (
    <div className="crop-modal-backdrop" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      <div className="crop-modal" onClick={e => e.stopPropagation()}>
        <div className="crop-modal-header">
          <h3>Ajuster votre photo</h3>
          <p>Glissez pour centrer votre visage</p>
          {error && <p className="crop-error" style={{color: '#dc3545', fontSize: '0.85rem', marginTop: '0.5rem'}}>{error}</p>}
          {!isImageLoaded && <p className="crop-loading" style={{color: '#666', fontSize: '0.85rem', marginTop: '0.5rem'}}>Chargement de l'image...</p>}
        </div>

        <div 
          className="crop-view-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
        >
          <div className="crop-circle-mask"></div>
          <img 
            ref={imageRef}
            src={image} 
            alt="Ajustement" 
            className="crop-image-preview"
            draggable="false"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              transform: `translate(calc(140px - 50% + ${position.x}px), calc(140px - 50% + ${position.y}px)) scale(${zoom})`,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              pointerEvents: 'none',
              opacity: isImageLoaded ? 1 : 0.5
            }}
          />
        </div>

        <div className="crop-controls-wrapper">
          <div className="crop-zoom-slider">
            <i className="fa-solid fa-minus small"></i>
            <input 
              type="range" 
              min="1" 
              max="3" 
              step="0.01" 
              value={zoom} 
              onChange={e => setZoom(parseFloat(e.target.value))}
              disabled={isUploading}
            />
            <i className="fa-solid fa-plus small"></i>
          </div>
          
          <div className="crop-footer-actions">
            <button className="btn-crop-dismiss" onClick={onCancel} disabled={isUploading}>
              Annuler
            </button>
            <button className="btn-crop-save" onClick={handleConfirm} disabled={isUploading || !isImageLoaded}>
              {isUploading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin me-2"></i>
                  Envoi...
                </>
              ) : !isImageLoaded ? 'Chargement...' : 'Valider'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
